import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Button, Input, Spinner, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { getMyTeamApi } from "../../../apiServices/adminWork.services";

const MyTeam = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState({ users: [], count: 0, totalAssignedLeads: 0 });
  const [searchQuery, setSearchQuery] = useState("");

  const authUser = JSON.parse(localStorage.getItem("authUser"))?.response || {};

  const fetchMyTeam = async (search = "") => {
    try {
      setLoading(true);
      const res = await getMyTeamApi({ search });
      if (res?.success) {
        setTeamData(res.data || { users: [], count: 0, totalAssignedLeads: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch team data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTeam(searchQuery);
  }, [searchQuery]);

  const filteredUsers = teamData.users || [];

  const getInitials = (firstName = "", lastName = "") => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "U";
  };

  return (
    <div className="page-content">
      <Breadcrumbs title="Team" breadcrumbItem="My Team" />

      {/* Top Metrics Cards */}
      <Row className="mb-4">
        <Col md={4} sm={6}>
          <Card className="border-0 shadow-sm bg-primary text-white">
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-white-50 small fw-semibold text-uppercase">Team Members</div>
                  <h2 className="mb-0 text-white fw-bold">{teamData.count || 0}</h2>
                </div>
                <div className="bg-white bg-opacity-25 d-flex align-items-center justify-content-center" style={{ width: "44px", height: "44px", flexShrink: 0, borderRadius: "50%" }}>
                  <i className="mdi mdi-account-group fs-3 text-white" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={4} sm={6}>
          <Card className="border-0 shadow-sm bg-success text-white">
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-white-50 small fw-semibold text-uppercase">Total Assigned Leads</div>
                  <h2 className="mb-0 text-white fw-bold">{teamData.totalAssignedLeads || 0}</h2>
                </div>
                <div className="bg-white bg-opacity-25 d-flex align-items-center justify-content-center" style={{ width: "44px", height: "44px", flexShrink: 0, borderRadius: "50%" }}>
                  <i className="mdi mdi-account-details-outline fs-3 text-white" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={4} sm={12}>
          <Card className="border-0 shadow-sm bg-info text-white">
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-white-50 small fw-semibold text-uppercase">Reporting Manager</div>
                  <h5 className="mb-0 text-white fw-bold truncate">
                    {authUser.firstName ? `${authUser.firstName} ${authUser.lastName || ""}` : "Manager"}
                  </h5>
                  <small className="text-white-50">{authUser.email}</small>
                </div>
                <div className="bg-white bg-opacity-25 d-flex align-items-center justify-content-center" style={{ width: "44px", height: "44px", flexShrink: 0, borderRadius: "50%" }}>
                  <i className="mdi mdi-shield-account-outline fs-3 text-white" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filter & Search Bar */}
      <div className="card p-3 mb-4 shadow-sm">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div style={{ minWidth: "280px", maxWidth: "420px", width: "100%" }}>
            <div className="position-relative">
              <i
                className="mdi mdi-magnify"
                style={{
                  position: "absolute",
                  top: "10px",
                  left: "12px",
                  fontSize: "16px",
                  color: "#74788d",
                }}
              />
              <Input
                placeholder="Search by user name, email, phone..."
                value={searchQuery}
                style={{ paddingLeft: "35px" }}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <Button
            color="light"
            onClick={() => fetchMyTeam(searchQuery)}
            className="d-flex align-items-center gap-1"
          >
            <i className="mdi mdi-refresh" /> Refresh
          </Button>
        </div>
      </div>

      {/* Team Cards Grid */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <div className="mt-2 text-muted fw-semibold">Loading team members...</div>
        </div>
      ) : filteredUsers.length > 0 ? (
        <Row>
          {filteredUsers.map((user) => (
            <Col xl={4} md={6} key={user._id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm card-hover transition-all position-relative overflow-hidden">
                <div
                  style={{
                    height: "6px",
                    background: user.isActive !== false ? "linear-gradient(90deg, #556ee6 0%, #34c38f 100%)" : "#74788d",
                  }}
                />

                <CardBody className="p-4 d-flex flex-column justify-content-between">
                  <div>
                    {/* Header profile info */}
                    <div className="d-flex align-items-start gap-3 mb-3" style={{ minHeight: "110px" }}>
                      <div
                        className="text-white d-flex align-items-center justify-content-center fw-bold shadow-sm"
                        style={{
                          width: "50px",
                          height: "50px",
                          minWidth: "50px",
                          minHeight: "50px",
                          flexShrink: 0,
                          borderRadius: "50%",
                          aspectRatio: "1 / 1",
                          fontSize: "18px",
                          background: "linear-gradient(135deg, #556ee6 0%, #74788d 100%)",
                          overflow: "hidden",
                        }}
                      >
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.firstName}
                            className="rounded-circle w-100 h-100 object-cover"
                          />
                        ) : (
                          getInitials(user.firstName, user.lastName)
                        )}
                      </div>

                      <div className="flex-grow-1 overflow-hidden">
                        <h5 className="mb-1 text-truncate fw-bold">
                          {user.firstName} {user.lastName}
                        </h5>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <Badge color="primary" className="bg-primary-subtle text-primary border border-primary-subtle">
                            {user.designationId?.title || user.designationId?.name || user.role || "Team Member"}
                          </Badge>
                          {user.departmentId?.name && (
                            <Badge color="light" className="text-muted border">
                              {user.departmentId.name}
                            </Badge>
                          )}
                        </div>
                        <small className="text-muted d-block text-truncate">
                          <i className="mdi mdi-email-outline me-1" />
                          {user.email}
                        </small>
                        {user.mobile && (
                          <small className="text-muted d-block text-truncate">
                            <i className="mdi mdi-phone-outline me-1" />
                            {user.mobile}
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Assigned Leads Badge */}
                    <div className="p-3 rounded bg-light mb-3 border">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="small text-muted fw-semibold">Assigned Active Leads</span>
                        <span className="badge bg-success-subtle text-success fs-6 px-3 py-1 rounded-pill fw-bold border border-success-subtle">
                          <i className="mdi mdi-account-search me-1" />
                          {user.assignedLeadsCount || 0} Leads
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Primary CTA Button */}
                  <Button
                    color="primary"
                    className="w-100 d-flex align-items-center justify-content-center gap-2 py-2 fw-semibold shadow-sm"
                    onClick={() => navigate(`/user-leads-page/${user._id}`)}
                  >
                    View Assigned Leads
                    <i className="mdi mdi-arrow-right" />
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="border-0 shadow-sm p-5 text-center">
          <CardBody>
            <i className="mdi mdi-account-search-outline fs-1 text-muted opacity-50 d-block mb-3" />
            <h5 className="fw-bold">No Team Members Found</h5>
            <p className="text-muted mb-0">
              There are no team members reporting under you currently or matching your search filter.
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default MyTeam;
